import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../lib/api';
import { qs } from '../lib/util';
import type { BrandListRow, BrandStats, SupplierSync, BrandView } from '../lib/types';
import { Chip, Empty, ErrorBox, Field, Loading, Modal, Pager, Seg, SourceChip, Table, Toolbar } from '../ui';
import BrandLogo from '../components/BrandLogo';

const PAGE = 50;
type Tab = 'unreviewed' | 'reviewed' | 'all';

/**
 * Sources offered when linking a brand by hand. A free-text datalist rather than
 * a closed list: a new catalogue should not need a UI change before an operator
 * can record what it calls a brand.
 */
const SOURCES = ['tecdoc', 'kromeda', 'internal', '*'];

/** The blast radius of a fuse: how many products currently resolve to this brand. */
function Radius({ n }: { n: number }) {
  return n > 0 ? <b>{n}</b> : <span className="text-faint">0</span>;
}

function Detail({ id, onClose, onChanged }: { id: number; onClose: () => void; onChanged: () => void }) {
  const { request } = useApi();
  const [v, setV] = useState<BrandView | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [nSource, setNSource] = useState('kromeda');
  const [nName, setNName] = useState('');
  const [nId, setNId] = useState('');

  const load = useCallback(async () => {
    const r = await request<BrandView>('GET', `/v1/brands/${id}`);
    if (r.data?.brand) setV(r.data);
  }, [request, id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    setErr('');
    const r = await request<{ ok?: boolean; error?: string }>('PATCH', `/v1/admin/brands/${id}`, body);
    setBusy(false);
    if (r.data?.error) setErr(r.data.error);
    else {
      await load();
      onChanged();
    }
  }

  /**
   * Attach or correct one database's name for this brand.
   *
   * The endpoint upserts on (source, name), so the same call records a new link
   * and edits an existing one's id — which is what makes the id cell editable in
   * place rather than needing a detach-and-retype.
   */
  async function saveIdentity(source: string, nameRaw: string, sourceId: string | null) {
    setBusy(true);
    setErr('');
    const r = await request<{ id?: number; error?: string }>('POST', `/v1/admin/brands/${id}/identities`, {
      source,
      nameRaw,
      ...(sourceId !== null ? { sourceId } : {}),
    });
    setBusy(false);
    if (r.data?.error) setErr(r.data.error);
    else {
      await load();
      onChanged();
    }
  }

  async function detach(identityId: number) {
    setBusy(true);
    await request('DELETE', `/v1/admin/brands/identities/${identityId}`);
    setBusy(false);
    await load();
    onChanged();
  }

  if (!v) return <Modal title="Brand" onClose={onClose}><Loading /></Modal>;

  return (
    <Modal title={`Brand — ${v.brand.displayName}`} onClose={onClose}>
      {err && <div className="mb-3"><ErrorBox>{err}</ErrorBox></div>}

      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
        <span className="mono text-muted">{v.brand.canonNorm}</span>
        <Chip>{v.brand.kind}</Chip>
        <span>
          <b>{v.productCount}</b> <span className="text-muted">products resolve here</span>
        </span>
        {v.brand.reviewed ? (
          <Chip className="text-ok">reviewed{v.brand.reviewedBy ? ` · ${v.brand.reviewedBy}` : ''}</Chip>
        ) : (
          <Chip className="text-warn">not reviewed</Chip>
        )}
      </div>

      <Field label="Display name">
        <div className="flex gap-2">
          <input
            className="input"
            defaultValue={v.brand.displayName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void patch({ displayName: (e.target as HTMLInputElement).value });
            }}
          />
        </div>
      </Field>

      <div className="mt-3">
        <div className="mb-1.5 text-[11.5px] text-faint">
          Identities — what each database calls this brand, and the id it files it under. That id is what a consumer
          joins on, so a blank one means rows we publish for this brand cannot be looked up on the other side.
        </div>
        <Table cols={['Source', 'Name as printed', 'Their id', '']}>
          {v.identities.map((i) => (
            <tr key={i.id}>
              <td>{i.source === '*' ? <Chip>any source</Chip> : <SourceChip source={i.source} />}</td>
              <td className="mono">{i.nameRaw}</td>
              <td>
                <input
                  className="input mono"
                  style={{ width: 120 }}
                  placeholder="—"
                  defaultValue={i.sourceId ?? ''}
                  disabled={busy}
                  title="That database's own id for this brand"
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    if (val && val !== (i.sourceId ?? '')) void saveIdentity(i.source, i.nameRaw, val);
                  }}
                />
              </td>
              <td className="text-right">
                <button className="btn btn-sm" disabled={busy} onClick={() => void detach(i.id)}>
                  Detach
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <input
                className="input mono"
                style={{ width: 110 }}
                list="cati-brand-sources"
                placeholder="source"
                value={nSource}
                onChange={(e) => setNSource(e.target.value)}
              />
              <datalist id="cati-brand-sources">
                {SOURCES.map((x) => (
                  <option key={x} value={x} />
                ))}
              </datalist>
            </td>
            <td>
              <input
                className="input mono"
                placeholder={v.brand.displayName}
                value={nName}
                onChange={(e) => setNName(e.target.value)}
              />
            </td>
            <td>
              <input
                className="input mono"
                style={{ width: 120 }}
                placeholder="their id"
                value={nId}
                onChange={(e) => setNId(e.target.value)}
              />
            </td>
            <td className="text-right">
              <button
                className="btn btn-sm btn-primary"
                disabled={busy || !nSource.trim()}
                onClick={async () => {
                  // Blank name means "they call it what we call it", which is the
                  // common case and saves retyping it for every source.
                  await saveIdentity(nSource.trim(), nName.trim() || v.brand.displayName, nId.trim() || null);
                  setNName('');
                  setNId('');
                }}
              >
                Link
              </button>
            </td>
          </tr>
        </Table>
      </div>

      <div className="mt-4 border-t border-line pt-3">
        <BrandLogo brandId={id} onChanged={onChanged} />
      </div>

      <Toolbar className="!mb-0 mt-3">
        <button
          className={`btn ${v.brand.reviewed ? '' : 'btn-primary'}`}
          disabled={busy}
          onClick={() => void patch({ reviewed: !v.brand.reviewed })}
        >
          {v.brand.reviewed ? 'Mark not reviewed' : 'Mark reviewed'}
        </button>
      </Toolbar>
    </Modal>
  );
}

export default function Brands() {
  const { request } = useApi();
  const [tab, setTab] = useState<Tab>('unreviewed');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<BrandListRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<BrandStats | null>(null);
  const [sel, setSel] = useState<number[]>([]);
  const [open, setOpen] = useState<number | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [sync, setSync] = useState<SupplierSync | null>(null);
  const [newName, setNewName] = useState('');
  const [idsOpen, setIdsOpen] = useState(false);
  const [idsText, setIdsText] = useState('');

  const load = useCallback(async () => {
    const reviewed = tab === 'all' ? undefined : tab === 'reviewed' ? '1' : '0';
    const r = await request<{ items: BrandListRow[]; total: number }>(
      'GET',
      `/v1/brands${qs({ q: q || undefined, reviewed, limit: PAGE, offset: page * PAGE })}`,
    );
    if (r.data?.items) {
      setRows(r.data.items);
      setTotal(r.data.total);
    }
    const s = await request<BrandStats>('GET', '/v1/brands/stats');
    if (s.data) setStats(s.data);
  }, [request, tab, q, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function harvest() {
    setBusy('Reading the brands already in the graph…');
    setErr('');
    const r = await request<{ brands?: number; identities?: number; error?: string }>(
      'POST',
      '/v1/admin/brands/harvest',
    );
    setBusy('');
    if (r.data?.error) setErr(r.data.error);
    else {
      setSel([]);
      await load();
    }
  }

  /**
   * Pull each brand's TecDoc supplier id from TecDoc's own directory.
   *
   * Worth its own button because the id is what decides whether a cross reference
   * we publish is usable: a consumer joins on (article number, supplier id), so a
   * brand without one has its crosses dropped on arrival. The result deliberately
   * reports what is STILL missing — those brands are outside the provider's TecDoc
   * licence and can only be filled in by hand.
   */
  async function syncSuppliers() {
    setBusy('Reading TecDoc’s supplier directory…');
    setErr('');
    const r = await request<SupplierSync & { error?: string }>('POST', '/v1/admin/brands/sync-suppliers');
    setBusy('');
    if (r.data?.error) {
      setErr(r.data.error);
      return;
    }
    setSync(r.data);
    await load();
  }

  /**
   * Record supplier ids pasted by hand, as `id;name` per line.
   *
   * Needed because a brand outside the provider's TecDoc licence has no id to
   * discover, yet the consumer still joins on one it allocated itself. Either
   * column order is accepted — the list arrives however somebody exported it —
   * by treating whichever field is numeric as the id.
   */
  /**
   * Create a brand by hand.
   *
   * Needed because a brand only appears here once something has SEEN it, and a
   * supplier we are about to import — or one that exists solely to carry a
   * catalogue's code for a brand — has been seen by nobody yet. Opens the new
   * brand straight away, since the reason for creating one is always to link an
   * identity to it.
   */
  async function createBrand() {
    const displayName = newName.trim();
    if (!displayName) return;
    setBusy('Creating…');
    setErr('');
    const r = await request<{ id?: number; error?: string }>('POST', '/v1/admin/brands', {
      displayName,
      kind: 'observed',
    });
    setBusy('');
    if (r.data?.error) {
      setErr(r.data.error);
      return;
    }
    setNewName('');
    await load();
    if (r.data?.id) setOpen(r.data.id);
  }

  async function saveIds() {
    const entries: { name: string; sourceId: string }[] = [];
    for (const raw of idsText.split(/\r?\n/)) {
      const line = raw.trim();
      if (!line) continue;
      const parts = line.split(/[;,\t]/).map((x) => x.trim());
      const a = parts[0];
      const b = parts[1];
      if (!a || !b) continue;
      if (/^(id|codice)$/i.test(a)) continue; // header row
      const numericFirst = /^\d+$/.test(a);
      const sourceId = numericFirst ? a : b;
      const name = numericFirst ? b : a;
      if (!/^\d+$/.test(sourceId)) continue;
      entries.push({ name, sourceId });
    }
    if (entries.length === 0) {
      setErr('Nothing parsed — expected one "id;name" per line.');
      return;
    }
    setBusy(`Recording ${entries.length} supplier ids…`);
    setErr('');
    const r = await request<SupplierSync & { error?: string }>('POST', '/v1/admin/brands/supplier-ids', {
      source: 'tecdoc',
      entries,
    });
    setBusy('');
    if (r.data?.error) {
      setErr(r.data.error);
      return;
    }
    setSync(r.data);
    setIdsOpen(false);
    setIdsText('');
    await load();
  }

  async function fuse() {
    if (sel.length < 2) return;
    setBusy('Fusing…');
    setErr('');
    const r = await request<{ ok?: boolean; error?: string }>('POST', '/v1/admin/brands/fuse', {
      ids: sel,
      reason: 'merged in the console',
    });
    setBusy('');
    if (r.data?.error) setErr(r.data.error);
    else {
      setSel([]);
      await load();
    }
  }

  const toggle = (id: number) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <>
      <div className="blk !mb-0">
        <div className="blk-head">
          <h3 className="text-[13px] font-semibold">Brands</h3>
          <span className="text-[11.5px] text-faint">
            {stats
              ? `${stats.total} brands · ${stats.unreviewed} awaiting review · ${stats.multiIdentity} with several identities`
              : 'one brand, many spellings'}
          </span>
        </div>
        <div className="blk-body">
          <Toolbar>
            <Seg
              value={tab}
              onChange={(v) => {
                setTab(v);
                setPage(0);
              }}
              options={[
                { value: 'unreviewed' as Tab, label: 'Needs review' },
                { value: 'reviewed' as Tab, label: 'Reviewed' },
                { value: 'all' as Tab, label: 'All' },
              ]}
            />
            <input
              className="input"
              style={{ width: 200 }}
              placeholder="Search a brand…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(0);
              }}
            />
            <span className="flex-1" />
            <button className="btn btn-primary" disabled={sel.length < 2 || !!busy} onClick={() => void fuse()}>
              {sel.length >= 2 ? `Fuse ${sel.length} brands` : 'Fuse'}
            </button>
            <button
              className="btn btn-sm"
              disabled={!!busy}
              title="Fill in each brand's TecDoc supplier id — the key a client joins our cross references on"
              onClick={() => void syncSuppliers()}
            >
              Sync TecDoc ids
            </button>
            <button
              className="btn btn-sm"
              disabled={!!busy}
              title="Paste ids for brands TecDoc does not list — one 'id;name' per line"
              onClick={() => setIdsOpen((v) => !v)}
            >
              Enter ids by hand
            </button>
            <input
              className="input"
              style={{ width: 150 }}
              placeholder="New brand name…"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void createBrand();
              }}
            />
            <button className="btn btn-sm" disabled={!!busy || !newName.trim()} onClick={() => void createBrand()}>
              Create brand
            </button>
            <button className="btn btn-sm" disabled={!!busy} onClick={() => void harvest()}>
              Harvest from graph
            </button>
          </Toolbar>

          {idsOpen && (
            <div className="mb-3 rounded border border-line bg-panel2 p-3">
              <div className="mb-1 text-[12px] text-muted">
                One <span className="mono">id;name</span> per line — for brands outside the provider's TecDoc licence,
                whose id exists only in the consuming system.
              </div>
              <textarea
                className="input mono w-full"
                rows={7}
                placeholder={'9001;SISTEMCAR'}
                value={idsText}
                onChange={(e) => setIdsText(e.target.value)}
              />
              <div className="mt-2 flex gap-2">
                <button className="btn btn-sm btn-primary" disabled={!!busy} onClick={() => void saveIds()}>
                  Save ids
                </button>
                <button className="btn btn-sm" onClick={() => setIdsOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {sync && (
            <div className="mb-3 rounded border border-line bg-panel2 p-3 text-[12px]">
              <div className="mb-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <b>TecDoc supplier ids</b>
                <span className="text-muted">
                  directory {sync.suppliers} · matched {sync.matched} · updated {sync.updated} · not ours{' '}
                  {sync.unknownToUs}
                </span>
                <span className={sync.coverage.withId === sync.coverage.total ? 'text-ok' : 'text-warn'}>
                  {sync.coverage.withId}/{sync.coverage.total} of our brands now have an id
                </span>
                <button className="btn btn-sm ml-auto" onClick={() => setSync(null)}>
                  Dismiss
                </button>
              </div>
              {sync.stillMissing.length > 0 && (
                <div className="text-faint">
                  <b>No TecDoc id ({sync.stillMissing.length})</b> — outside the provider's licence, so their crosses
                  cannot be joined until an id is entered by hand on the brand:{' '}
                  <span className="mono">{sync.stillMissing.slice(0, 40).join(', ')}</span>
                  {sync.stillMissing.length > 40 && ` … +${sync.stillMissing.length - 40} more`}
                </div>
              )}
            </div>
          )}

          {sel.length >= 2 && (
            <div className="mb-2 text-[11.5px] text-muted">
              Fusing keeps the oldest brand and moves the others’ identities onto it. Reversible — every fuse is in the
              audit trail.
            </div>
          )}
          {busy && <div className="mb-3"><Loading label={busy} /></div>}
          {err && <div className="mb-3"><ErrorBox>{err}</ErrorBox></div>}

          {rows === null ? (
            <Loading />
          ) : rows.length === 0 ? (
            <Empty>{tab === 'unreviewed' ? 'Nothing awaiting review.' : 'No brands.'}</Empty>
          ) : (
            <>
              <Table cols={['', 'Brand', 'Canonical', 'Identities', 'Seen in', 'Products', 'Reviewed']}>
                {rows.map((b) => (
                  <tr key={b.id} className="cursor-pointer" onClick={() => setOpen(b.id)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={sel.includes(b.id)} onChange={() => toggle(b.id)} />
                    </td>
                    <td>{b.displayName}</td>
                    <td className="mono text-[11.5px] text-muted">{b.canonNorm}</td>
                    <td>{b.identityCount > 1 ? <b>{b.identityCount}</b> : b.identityCount}</td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {b.sources.map((s) => (s === '*' ? <Chip key={s}>any</Chip> : <SourceChip key={s} source={s} />))}
                      </div>
                    </td>
                    <td><Radius n={b.productCount} /></td>
                    <td>{b.reviewed ? <Chip className="text-ok">yes</Chip> : <Chip className="text-warn">no</Chip>}</td>
                  </tr>
                ))}
              </Table>
              <Pager total={total} limit={PAGE} offset={page * PAGE} onOffset={(o: number) => setPage(Math.floor(o / PAGE))} />
            </>
          )}
        </div>
      </div>

      {open !== null && <Detail id={open} onClose={() => setOpen(null)} onChanged={() => void load()} />}
    </>
  );
}
